import { PrefixCurrencyType } from '../components/ProductCard/constants';
import { CmsGift, CmsProduct, GiftListItem } from '../types/cmsProduct';
import { LoanSearchParams } from './searchParams/loanSearchParams';

export type RewardSourceProduct = Pick<
  CmsProduct,
  'exclusive_offers' | 'bundle_exclusive_offers' | 'welcome_offers'
> & {
  hot_reward?: CmsGift;
};

export const getHighersRewardExclusiveOffersAndWelcomeOffersGitsFromProduct = (
  product: RewardSourceProduct,
  loanSearchParams?: LoanSearchParams,
  ignoreFilter?: boolean,
): {
  price: number;
  descriptions: string[];
  prefix: PrefixCurrencyType;
} | null => {
  const allOffers = [];
  const exclusiveOffers = [
    ...(product?.exclusive_offers ?? []),
    ...(product?.bundle_exclusive_offers ?? []),
  ].flatMap((offer) =>
    (offer?.list_of_gifts ?? []).map((gift) => ({
      gift,
      offerUid: offer?.uid,
    })),
  ) as { offerUid: string; gift: GiftListItem }[];
  allOffers.push(...exclusiveOffers);

  const exclusiveGifts = exclusiveOffers.map((offer) => offer).filter(Boolean);

  exclusiveGifts.sort(
    (a, b) =>
      Number(b?.gift?.gift?.gift_value?.price ?? 0) -
      Number(a?.gift?.gift?.gift_value?.price ?? 0),
  );

  const welcomeOffers = [
    ...(product?.welcome_offers?.flatMap((offer) =>
      (offer?.list_of_gifts ?? []).map((gift) => ({
        gift,
        offerUid: offer?.uid,
      })),
    ) ?? []),
  ] as { offerUid: string; gift: GiftListItem }[];
  allOffers.push(...welcomeOffers);

  const hotRewardOfferInOffers = allOffers.find(
    (off) => off.gift.gift?.uid === product.hot_reward?.uid,
  );
  const hotRewardGiftFromOffer = hotRewardOfferInOffers?.gift?.gift;
  const topUpValue = hotRewardGiftFromOffer?.gift_value?.topup_amount ?? 0;

  if (hotRewardGiftFromOffer) {
    return {
      price: (hotRewardGiftFromOffer?.gift_value?.price ?? 0) - topUpValue,
      descriptions: [hotRewardGiftFromOffer?.gift_title],
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      prefix: hotRewardGiftFromOffer?.gift_value?.prefix!,
    };
  }

  const rewardsCombined = [...exclusiveGifts, ...welcomeOffers]
    .filter(Boolean)
    .filter((gift_: { offerUid: string; gift: GiftListItem }) => {
      const gift = gift_.gift;
      if (ignoreFilter) {
        return true;
      }

      const criteriaList = gift.criteria_list ?? [];

      const amountCriteria = criteriaList?.find(
        (crit) => crit?.name?.name === 'ELIGIBLE_LOAN_RANGE',
      );
      const loanCriteria = criteriaList?.find(
        (crit) => crit?.name?.name === 'ELIGIBLE_TENURE_RANGE',
      );
      let isAmountCriteriaPassed = true;
      let isTenureCriteriaPassed = true;
      if (amountCriteria && loanSearchParams) {
        isAmountCriteriaPassed =
          +(amountCriteria.value_end ?? Infinity) >= +loanSearchParams.amount &&
          +(amountCriteria.value_start ?? 0) <= +loanSearchParams.amount;
      }

      if (loanCriteria && loanSearchParams) {
        isTenureCriteriaPassed =
          +(loanCriteria.value_end ?? Infinity) >= +loanSearchParams.tenure &&
          +(loanCriteria.value_start ?? 0) <= +loanSearchParams.tenure;
      }

      return (
        criteriaList?.length === 0 ||
        (isTenureCriteriaPassed && isAmountCriteriaPassed)
      );
    });

  const result = rewardsCombined.reduce((acc, cur) => {
    const newAcc = acc ?? {
      offerUids: new Set<string>(),
    };

    if ((newAcc.offerUids as Set<string>).has(cur.offerUid)) {
      return newAcc;
    }

    newAcc.price =
      (newAcc.price ?? 0) + (cur?.gift?.gift?.gift_value?.price ?? 0);
    if (cur?.gift?.gift?.gift_value?.prefix) {
      newAcc.prefix = cur?.gift?.gift?.gift_value?.prefix;
    }
    if (!newAcc.descriptions?.length) {
      newAcc.descriptions = [];
    }

    newAcc.offerUids.add(cur.offerUid);
    newAcc.descriptions.push(cur.gift.gift?.gift_title);
    return newAcc;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, null as any);

  if (result?.price) {
    result.price = result.price - topUpValue;
  }

  return result as {
    price: number;
    descriptions: string[];
    offerUids: Set<string>;
    prefix: PrefixCurrencyType;
  };
};
